import React, { ChangeEvent, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from 'styled-components';
import { ParsedSuggestion, SuggestionType } from '@dazzle/api-client';

import { useModalContext } from 'Common/contexts/ModalContext';
import Modal from 'Common/components/Modal';
import { fromSuggestion, USuggestion } from 'Common/util/Suggestion';

import {
  CardBody,
  CardHeader,
  CardLabel,
  CardRow,
  CardValue,
  Cell,
  DesktopRow,
  MobileCard,
  ProductTitle
} from 'Compass/components/orders/CommonRow';
import RelativeTime from 'Compass/components/styled/RelativeTime';
import { Button, ButtonTheme } from 'Compass/components/styled/Button';

const formatterUSD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const SuggestionRow = ({ showActions, parsedSuggestion, selected, onSelect, onApprove, onReject, onClick }: {
  showActions: boolean;
  parsedSuggestion: ParsedSuggestion;
  selected: boolean;
  onSelect: (suggestion: USuggestion, selected: boolean) => void;
  onApprove: (suggestion: USuggestion) => void;
  onReject: (suggestion: USuggestion) => void;
  onClick?: (suggestion: USuggestion) => void;
}) => {
  const navigate = useNavigate();
  const modalContext = useModalContext();

  const suggestion = fromSuggestion(parsedSuggestion.suggestion);
  const content = suggestion.content;

  if (content === undefined) {
    return null;
  }

  const title = suggestion.title?.split(' ').slice(0, 5).join(' ');
  const price =
    content.type === SuggestionType.Product && content.event?.price !== null && content.event?.price !== undefined
      ? formatterUSD.format(content.event.price)
      : '';

  const onLabelClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const onCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    onSelect(suggestion, e.currentTarget.checked);
  };

  const onImageClick = (e: MouseEvent, imageUrl: string) => {
    e.preventDefault();
    e.stopPropagation();

    modalContext.show({
      contents: <Modal onClickOutside={modalContext.hide} maxWidth="800px">
        <img src={imageUrl} />
      </Modal>
    });
  };

  const onApproveInternal = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onApprove(suggestion);
  };

  const onRejectInternal = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onReject(suggestion);
  };

  const handleClick = () => {
    if (onClick) {
      onClick(suggestion);
    } else {
      navigate(`/order_suggestion/${suggestion.suggestionId}`);
    }
  };

  return <>
    <DesktopRow onClick={handleClick}>
      {showActions && <Cell as="label" htmlFor={`suggestion_${suggestion.suggestionId}`} onClick={onLabelClick}>
        <input type="checkbox" id={`suggestion_${suggestion.suggestionId}`} onChange={onCheckboxChange}
          checked={selected} />
      </Cell>}
      <Cell>
        {parsedSuggestion.photo_url ? <ProductImage src={parsedSuggestion.photo_url} onClick={(e) => onImageClick(e,
          parsedSuggestion.photo_url ?? '')} /> : null}
      </Cell>
      <Cell>
        <ProductTitle>{title}</ProductTitle>
      </Cell>
      <Cell>
        <ProductTitle>{content.message}</ProductTitle>
      </Cell>
      <Cell $alignRight={true}>{price}</Cell>
      <Cell><RelativeTime date={(suggestion.sortKey || 0) * 1000} /></Cell>
      {showActions && <Cell>
        <ButtonGroup>
          <Button $theme={ButtonTheme.secondary} onClick={onRejectInternal}>
            Reject
          </Button>
          <Button onClick={onApproveInternal}>
            Approve
          </Button>
        </ButtonGroup>
      </Cell>}
    </DesktopRow>
    <MobileCard onClick={handleClick}>
      <CardHeader>
        <ProductTitle>{title}</ProductTitle>
      </CardHeader>
      <CardBody>
        <Cell>
          {parsedSuggestion.photo_url ? <ProductImage src={parsedSuggestion.photo_url} onClick={(e) => onImageClick(e,
            parsedSuggestion.photo_url ?? '')} /> : null}
        </Cell>
        <CardRow>
          <ProductTitle>{content.message}</ProductTitle>
        </CardRow>
        <CardRow>
          <CardLabel>Est. Price</CardLabel>
          <CardValue>{price}</CardValue>
        </CardRow>
        <CardRow>
          <CardLabel>Updated</CardLabel>
          <CardValue><RelativeTime date={suggestion.lastUpdatedTs} /></CardValue>
        </CardRow>
        {showActions && <CardRow>
          <ButtonGroup>
            <Button $theme={ButtonTheme.secondary} onClick={onRejectInternal}>
              Reject
            </Button>
            <Button onClick={onApproveInternal}>
              Approve
            </Button>
          </ButtonGroup>
        </CardRow>}
      </CardBody>
    </MobileCard>
  </>;
};

const ProductImage = styled.img`
  margin: auto;
  height: 100px;
  width: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
`;

export default SuggestionRow;
